import { Minigrid } from "../types/minigridtypes";

// Code d'installation MJB (contrat identifiant_installation v1) : émis par la
// 8000, posé sur le site par FieldMate, l'import ou un technicien. minigridui
// ne le fabrique jamais, il le reçoit et le contrôle.
const INSTALLATION_CODE_PATTERN = /^(MG|CI|SO)-[A-Z]{2}-[0-9]{4,}$/;

// Un technicien qui tape « mg-bj-0042 » désigne bien MG-BJ-0042.
export const normalizeInstallationCode = (raw: string | null | undefined): string =>
  (raw ?? "").trim().toUpperCase();

export const isValidInstallationCode = (code: string): boolean =>
  INSTALLATION_CODE_PATTERN.test(code);

// Lien vers l'installation dans FieldMate, qui accepte le code dans son URL.
// null sans REACT_APP_FIELDMATE_URL (variable fixée au build) : pas de lien
// plutôt qu'un lien cassé.
export const fieldmateInstallationUrl = (
  code: string | null | undefined,
  baseUrl: string | undefined = process.env.REACT_APP_FIELDMATE_URL,
): string | null => {
  const c = normalizeInstallationCode(code);
  const base = (baseUrl ?? "").trim().replace(/\/+$/, "");
  if (!c || !base) return null;
  return `${base}/referentiel/installations/${encodeURIComponent(c)}`;
};

// Le site inscrit qui correspond au nom libre « Nom du minigrid » d'un
// compteur : son nom ou l'un de ses noms liés, sans casse ni espaces de bord.
// Même règle que la colonne Minigrid de la liste des compteurs ; le code d'un
// compteur est celui de ce site, jamais une copie.
export const findRegisteredMinigrid = (
  minigrids: Minigrid[] | null | undefined,
  rawName: string | null | undefined,
): Minigrid | undefined => {
  const target = (rawName ?? "").trim().toLowerCase();
  if (!target) return undefined;
  return (minigrids ?? []).find((mg) => {
    if ((mg.minigrid_name ?? "").trim().toLowerCase() === target) return true;
    return (mg.minigrid_linked_location_names ?? []).some(
      (n) => (n ?? "").trim().toLowerCase() === target,
    );
  });
};

// Table « nom libre (minuscules) → site » : même règle que
// findRegisteredMinigrid (le premier site trouvé gagne, avec ou sans code),
// pour que la colonne et le filtre donnent toujours le même site.
export const registeredMinigridByName = (
  minigrids: Minigrid[] | null | undefined,
): Record<string, Minigrid> => {
  const map: Record<string, Minigrid> = {};
  const add = (label: string | null | undefined, mg: Minigrid) => {
    const key = (label ?? "").trim().toLowerCase();
    if (key && !(key in map)) map[key] = mg;
  };
  for (const mg of minigrids ?? []) {
    // Même ordre que findRegisteredMinigrid : un site est essayé sur son nom
    // puis ses noms liés avant de passer au suivant.
    add(mg.minigrid_name, mg);
    for (const n of mg.minigrid_linked_location_names ?? []) add(n, mg);
  }
  return map;
};

// Décision du formulaire : quel code rattacher après l'enregistrement du
// site. Un site déjà relié garde son code (rien à faire) ; un champ vide ne
// rattache rien ; un code hors format bloque l'envoi.
export const planInstallationCodeLink = (
  savedCode: string | null | undefined,
  input: string | null | undefined,
): { wantedCode: string; error?: string } => {
  if (savedCode) return { wantedCode: "" };
  const wantedCode = normalizeInstallationCode(input);
  if (wantedCode && !isValidInstallationCode(wantedCode)) {
    return { wantedCode: "", error: "Code d'installation hors format (attendu MG|CI|SO-PAYS-NNNN, ex. MG-BJ-0042)." };
  }
  return { wantedCode };
};

// Le code ne part jamais par la création ou la modification génériques (le
// serveur l'ignorerait, mais un envoi qui le porte est un piège) : seule la
// route dédiée le pose.
export const withoutInstallationCode = <T extends Partial<Minigrid>>(data: T): T => {
  const {
    minigrid_installation_code: _code,
    minigrid_installation_linked_at: _at,
    minigrid_installation_link_source: _src,
    ...rest
  } = data;
  return rest as T;
};

// Corps d'erreur de PUT /minigrids/{id}/installation-code.
export type InstallationCodeErrorBody = {
  message?: string;
  current_installation_code?: string;
  conflict_minigrid_id?: string;
};

// Message lisible pour une réponse d'erreur du rattachement
// (PUT /minigrids/{id}/installation-code).
export const installationCodeErrorMessage = (
  status: number | undefined,
  body: InstallationCodeErrorBody | undefined,
  siteNameById?: (id: string) => string | undefined,
): string => {
  if (status === 409 && body?.current_installation_code) {
    return `Ce site porte déjà le code ${body.current_installation_code} : un code n'est jamais remplacé depuis l'interface.`;
  }
  if (status === 409 && body?.conflict_minigrid_id) {
    const name = siteNameById?.(body.conflict_minigrid_id);
    return `Ce code est déjà porté par le site ${name ?? body.conflict_minigrid_id}.`;
  }
  if (status === 409) {
    return body?.message ?? "Ce code est déjà porté par un autre site.";
  }
  if (status === 400) {
    return "Code d'installation hors format (attendu MG|CI|SO-PAYS-NNNN, ex. MG-BJ-0042).";
  }
  return body?.message ?? "Le code d'installation n'a pas pu être rattaché.";
};
